import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { getLink } from '_utils/helpers/link';
import { getInternalRoute } from '../shared/courseEventLinks';
import { normalizeJssFields } from '_utils/helpers/jss';
import Icon from '_components/Icon';
import { MaterialIcons } from '_components/Icon/definitions';
import { canUseDOM } from 'exenv';
import { withDigitalDataContext } from '_containers/DigitalDataContext';
import { emitTrackEvent, setObjectData } from '_utils/helpers/analytics';
import { DropdownRibbonWithCtaProps } from './definitions';
import {
  Container,
  Content,
  HeaderRow,
  LeftSection,
  DropdownTrigger,
  ArrowIcon,
  CityText,
  ButtonWrapper,
  Button,
  MetaRow,
  MetaItem,
  MetaIcon,
  DropdownPanel,
  DropdownList,
  DropdownListItem,
  DropdownLink,
  DropdownLinkText,
} from './StyledDropdownRibbonWithCta';

const DropdownRibbonWithCta: React.FC<DropdownRibbonWithCtaProps> = ({
  rendering,
  digitalData,
  setDigitalData,
}) => {
  const datasource = normalizeJssFields(rendering?.fields?.data?.datasource) || {};
  const currentCity = datasource?.currentCity?.value || '';
  const dateTag = datasource?.dateTag?.value || '';
  const locationTag = datasource?.locationTag?.value || '';
  const dateIcon = datasource?.dateIcon;
  const locationIcon = datasource?.locationIcon;
  const actionLink = getLink(datasource?.callToAction?.value);
  const rawLocations = datasource?.locations || [];

  const getFullUrl = (href: string) => {
    if (!href) {
      return '';
    }

    if (href.startsWith('/') && canUseDOM) {
      return `${window.location.origin}${href}`;
    }

    return href;
  };

  const handleCtaClick = () => {
    if (typeof setDigitalData === 'function' && actionLink) {
      const isInternal = actionLink.url ? actionLink.url.startsWith('/') : true;
      const destinationUrl = getFullUrl(actionLink.url || '');
      const buttonText = actionLink.text || 'Get your tickets now';

      const updatedDigitalData = setObjectData(
        ['component'],
        {
          name: buttonText,
          type: 'button',
          location: 'header',
        },
        setObjectData(
          ['interaction'],
          {
            type: 'click',
            detail: `${buttonText} CTA clicked`,
          },
          setObjectData(
            ['link'],
            {
              name: buttonText,
              type: isInternal ? 'internal' : 'external',
              location: 'body',
              destinationType: isInternal ? 'internal_navigation' : 'external_navigation',
              destinationUrl,
            },
            digitalData || {}
          )
        )
      );

      setDigitalData(updatedDigitalData);

      if (canUseDOM) {
        window.digitalData = updatedDigitalData;
      }

      emitTrackEvent('interaction');
    }
  };

  const [selectedLocationId, setSelectedLocationId] = useState('current-city');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();

  useEffect(() => {
    setSelectedLocationId('current-city');
  }, [currentCity]);

  const normalizedLocations = rawLocations.map((item) => ({
    id: item.id,
    location: normalizeJssFields(item.location),
  }));

  const locations = [
    ...(currentCity
      ? [
        {
          id: 'current-city',
          location: {
            value: {
              text: currentCity,
            },
          },
        },
      ]
      : []),
    ...normalizedLocations.filter((item) => item.location?.value?.text !== currentCity),
  ];
  const getSelectedLocationText = () => {
    const selected = locations.find((item) => item.id === selectedLocationId);
    return selected?.location?.jss?.value?.text || selected?.location?.value?.text || currentCity || 'Select city';
  };

  const handleDropdownCollapse = () => {
    if (typeof setDigitalData === 'function') {
      const componentName = getSelectedLocationText();
      const updatedDigitalData = setObjectData(
        ['component'],
        {
          name: componentName,
          type: 'dropdown',
          location: 'header',
        },
        setObjectData(
          ['interaction'],
          {
            type: 'collapse',
            detail: 'Dropdown collapsed',
          },
          setObjectData(
            ['link'],
            {
              name: '',
              type: '',
              location: '',
              destinationType: '',
              destinationUrl: '',
            },
            digitalData || {}
          )
        )
      );

      setDigitalData(updatedDigitalData);

      if (canUseDOM) {
        window.digitalData = updatedDigitalData;
      }

      emitTrackEvent('interaction');
    }
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => {
      const nextOpen = !prev;
      if (!nextOpen) {
        handleDropdownCollapse();
      }
      return nextOpen;
    });
  };

  const handleSelectLocation = (locationId: string) => {
    setSelectedLocationId(locationId);
    setIsOpen(false);
  };

  const collapseRef = useRef(handleDropdownCollapse);
  collapseRef.current = handleDropdownCollapse;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen((prev) => {
          if (prev) {
            collapseRef.current();
          }
          return false;
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const iconName = (value: any, fallback: string) => {
    if (value?.name) {
      return value.name as MaterialIcons;
    }
    return fallback as MaterialIcons;
  };

  return (
    <Container data-component id={rendering?.uid} ref={containerRef}>
      <Content>
        <HeaderRow>
          <LeftSection>
            <DropdownTrigger
              type="button"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              onClick={toggleDropdown}
            >
              <ArrowIcon isOpen={isOpen}>
                <Icon name={'expand_more' as MaterialIcons} />
              </ArrowIcon>
              <CityText>{getSelectedLocationText()}</CityText>
            </DropdownTrigger>
            <MetaRow>
              <MetaItem>
                <MetaIcon>
                  <Icon name={iconName(dateIcon, 'calendar_today')} />
                </MetaIcon>
                {dateTag}
              </MetaItem>
              <MetaItem>
                <MetaIcon>
                  <Icon name={iconName(locationIcon, 'place')} />
                </MetaIcon>
                {locationTag}
              </MetaItem>
            </MetaRow>
          </LeftSection>
          {actionLink && (
            <ButtonWrapper>
              <Button
                href={actionLink.url}
                target={actionLink.target || undefined}
                rel={actionLink.rel || undefined}
                onClick={handleCtaClick}
              >
                {actionLink.text || 'Get your tickets now'}
              </Button>
            </ButtonWrapper>
          )}
           {isOpen && (
        <DropdownPanel>
          <DropdownList role="listbox">
            {(() => {
              const selectedIndex = locations.findIndex((l) => l.id === selectedLocationId);
              const ordered = selectedIndex > -1 ? [locations[selectedIndex], ...locations.filter((_, i) => i !== selectedIndex)] : locations;
              return ordered.map((item) => {
                const itemLink = getLink(item.location?.value);
                const itemHref = itemLink?.url || item.location?.jss?.value?.href || item.location?.value?.url || '#';
                const itemTarget = itemLink?.target || item.location?.jss?.value?.target || undefined;
                const itemRel = itemLink?.rel || item.location?.jss?.value?.rel || undefined;
                const itemLabel = item.location?.value?.text || item.location?.jss?.value?.text || 'Other city name';
                const selected = item.id === selectedLocationId;
                return (
                  <DropdownListItem key={item.id}>
                    <DropdownLink
                      href={itemHref}
                      target={itemTarget}
                      rel={itemRel}
                      selected={selected}
                      data-selected={selected ? 'true' : 'false'}
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        handleSelectLocation(item.id);

                        if (itemHref === '#') {
                          e.preventDefault();
                          return;
                        }

                        const internalRoute = getInternalRoute(itemHref);
                        if (internalRoute) {
                          e.preventDefault();
                          history.push(internalRoute);
                        }
                      }}
                    >
                      <DropdownLinkText>{itemLabel}</DropdownLinkText>
                    </DropdownLink>
                  </DropdownListItem>
                );
              });
            })()}
          </DropdownList>
        </DropdownPanel>
      )}
        </HeaderRow>
       
      </Content>

      
    </Container>
  );
};

export default withDigitalDataContext(DropdownRibbonWithCta as any);
